import type { CSSProperties } from 'react';
import { CHROME_INSET_VAR } from 'hooks/useChromeInset';
import { ZIndex } from 'styles/zIndex';

/**
 * The one box Game Chat and Hand History share.
 *
 * Both panels open from neighbouring footer buttons and take turns in the same
 * slot, so they read their geometry from here instead of each carrying its own
 * numbers. The box is defined by its EDGES, not a fixed height: it starts just
 * under the top chrome (so the Exit Table / Sit Out buttons stay visible) and
 * ends just above the action footer, whatever the screen size.
 *
 * Single view: the table header publishes its measured height as the
 * `--table-chrome-inset` custom property on <html> (hooks/useChromeInset), the
 * same value the felt reserves. The Sit Out / Exit Table rail sits inside that
 * band. The fallback is COMPACT_HEADER_MIN_HEIGHT for hosts with no header
 * (storybook, tests).
 *
 * Grid cells: each cell has its own rail (`gridTableActionRail` in
 * pages/Game.tsx: top 8px, two 32px buttons, 8px gap) and a fixed 64px action
 * footer (GRID_FOOTER_HEIGHT). The panel has to paint in FRONT of every in-cell
 * control - the footer button groups (z-index 15) and that rail (z-index 40) -
 * so grid mode lifts to 50; ZIndex.overlay (11) is below both.
 */
export const GRID_PANEL_Z_INDEX = 50;

/** GRID_FOOTER_HEIGHT (64px) plus an 8px gap; Chat.gridLayout.test pins this. */
export const GRID_FOOTER_CLEARANCE_PX = 72;

/** gridTableActionRail (8 + 32 + 8 + 32 = 80px) plus 12px of breathing room. */
export const GRID_RAIL_CLEARANCE_PX = 92;

/** Clears the single-view action tray. */
export const SINGLE_VIEW_BOTTOM_PX = 70;

/** Gap between the header band and the panel's top edge. */
export const SINGLE_VIEW_TOP_GAP_PX = 12;

/** COMPACT_HEADER_MIN_HEIGHT - used only when no TableHeader has published its height. */
export const CHROME_INSET_FALLBACK_PX = 96;

export const singleViewTop = () =>
  `calc(env(safe-area-inset-top, 0px) + var(${CHROME_INSET_VAR}, ${CHROME_INSET_FALLBACK_PX}px) + ${SINGLE_VIEW_TOP_GAP_PX}px)`;

/**
 * One width everywhere. The panel is as wide as it is in single view on a wide
 * screen (500px, EN-11110: was 420px) and never wider - a 2-, 3- or 4-up grid
 * cell no longer stretches it to the cell. It shrinks only when the cell or
 * viewport is narrower than that, keeping the same side gutter. Game Chat
 * shares it, so the two panels stay siblings.
 */
export const PANEL_MAX_WIDTH_PX = 500;

/** Each panel sits by its own footer button: Hand History left, Game Chat right. */
export type CommsPanelSide = 'left' | 'right';

export interface CommsPanelOptions {
  /** Height the on-screen keyboard overlaps the viewport bottom (hooks/useKeyboardOffset). */
  keyboardOffset?: number;
  /** Which footer button the panel belongs to. Default left (Hand History). */
  side?: CommsPanelSide;
}

export const getCommsPanelStyle = (
  isGridMode: boolean,
  isVisible: boolean,
  { keyboardOffset = 0, side = 'left' }: CommsPanelOptions = {}
): CSSProperties => {
  const gutter = isGridMode ? '0.5rem' : '1rem';
  return {
    position: isGridMode ? 'absolute' : 'fixed',
    [side]: gutter,
    top: isGridMode ? `${GRID_RAIL_CLEARANCE_PX}px` : singleViewTop(),
    bottom: isGridMode
      ? `${GRID_FOOTER_CLEARANCE_PX}px`
      : `${SINGLE_VIEW_BOTTOM_PX + Math.max(0, keyboardOffset)}px`,
    width: `min(${PANEL_MAX_WIDTH_PX}px, calc(100% - ${gutter} * 2))`,
    zIndex: isGridMode ? GRID_PANEL_Z_INDEX : ZIndex.overlay,
    visibility: isVisible ? 'visible' : 'hidden'
  };
};
